/**
 * Authentication Service
 * 
 * Handles passcode verification against environment variable.
 * This service can be extended to support API-based authentication in the future.
 */

/**
 * Result of authentication attempt
 */
export interface AuthResult {
  /**
   * Whether authentication was successful
   */
  success: boolean;
  /**
   * Error message if authentication failed
   */
  message?: string;
}

/**
 * Authentication service for passcode verification
 */
export const authService = {
  /**
   * Verify passcode against expected value from environment
   * 
   * @param passcode - The passcode to verify
   * @returns Promise resolving to authentication result
   * 
   * @example
   * const result = await authService.login('mypasscode');
   * if (result.success) {
   *   // Authentication successful
   * } else {
   *   // Show error: result.message
   * }
   */
  login: async (passcode: string): Promise<AuthResult> => {
    // Artificial delay provides better UX by preventing jarring instant responses and showing the loading state
    // This 300ms delay makes the authentication feel more deliberate and allows users to see feedback
    await new Promise(resolve => setTimeout(resolve, 300));

    const expectedPasscode = import.meta.env.VITE_KXD_ACCESS_PASSCODE;

    if (!expectedPasscode) {
      console.error('VITE_KXD_ACCESS_PASSCODE environment variable is not set');
      return {
        success: false,
        message: 'Authentication system is not properly configured. Please contact your administrator.',
      };
    }

    if (passcode === expectedPasscode) {
      return {
        success: true,
      };
    }

    return {
      success: false,
      message: 'Invalid passcode. Please try again.',
    };
  },

  /**
   * Check if authentication is configured
   * 
   * @returns true if passcode environment variable is set
   */
  isConfigured: (): boolean => {
    return !!import.meta.env.VITE_KXD_ACCESS_PASSCODE;
  },
};
